import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import {
    Button,
    Icon,
    InputAdornment,
    LinearProgress,
    Typography,
    FormHelperText,
    MenuItem,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CloudDownload, OpenInNew } from '@material-ui/icons';
import { saveAs } from 'file-saver';
import _ from 'lodash';

import AdminPage from '../AdminPage';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import Actions from '../../page-framework/actions/Actions';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import TestTypeDeleteDialog from './TestTypeDeleteDialog';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import ListFormField from '../../form/ListFormField';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import NgpTextarea from '../../form/NgpTextarea';
import NgpFileUpload from '../../form/NgpFileUpload';
import NgpToggle from '../../form/NgpToggle';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteWrapper } from '../../../services/FormDeleteService';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useFind } from '../../../services/FormQueryService';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { throwValidationError, useDataService } from '../../../services/ApiDataService';
import useAlerts from '../../../stores/AlertsStore';
import { RowFlexLayout } from '../../layout';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import { ADMIN_TEST_TYPES_URL } from './TestTypesAdminConstants';
import { convertScript, disableTestsForType, hasTestInstancesForType } from './TestTypesApi';
import MetricsDialogForm from './MetricsDialogForm';
import ThresholdsDialogForm from './ThresholdsDialogForm';
import TestInstanceTemplateSection from './TestInstanceTemplateSection';
import NgpNativeNumberField from '../../form/NgpNativeNumberField';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    TEST_TYPE_ICON_FIELD,
    TEST_TYPE_DESCRIPTION_FIELD,
    TEST_TYPE_TIMEOUT_FIELD,
    TEST_TYPE_TITLE_FIELD,
    TEST_TYPE_SLICE_PACKETS_FIELD,
    TEST_TYPE_BTT_TEST_FIELD,
    TEST_TYPE_SHOW_BTT_AUTH_FIELD,
    TEST_TYPE_SCRIPT_FIELD,
    TEST_TYPE_METRICS_FIELD,
    TEST_TYPE_THRESHOLDS_FIELD,
    TEST_TYPE_CHART_Y_AXIS_FIELD,
    TEST_TYPE_CHART_Y_AXIS_TITLE_LEFT_FIELD,
    TEST_TYPE_CHART_Y_AXIS_TITLE_RIGHT_FIELD,
    TEST_TYPE_CHART_TYPE_FIELD,
    TEST_TYPE_CHART_STACKING_FIELD,
    TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD,
    EXAMPLE_SCRIPT_TITLE,
    RESERVED_METRICS,
    CHART_TYPES,
    CHART_STACKING_DEFAULT_KEY,
    CHART_STACKINGS,
    BASE_INPUT_TYPES,
    WIFI_INPUT_TYPES,
    metricColumns,
    getThresholdColumns,
    defaultValues,
} from './TestTypesDetailPageConstants';
import {
    buildExampleScript,
    normalizeDownloadBlob,
    isBttScript,
    cleanUpAxesAndMetrics,
    buildTestTypePayload,
    mergeMetricsFromScript,
    toLowerCamelCase,
} from './TestTypesDetailPageHelper';
import { isUUID } from '../tests/detail/hooks/UseServiceTestTypeMapping';
import TestIdPage from '../tests/TestIdPage';

const functionName = AdminPages.ServiceTests;
const goToUrl = '../';
const MAX_SCRIPT_FILE_SIZE_ALLOWED = 2.202e7;

const useStyles = makeStyles((theme) => ({
    iconField: {
        '& .MuiInput-underline:before, & .MuiInput-underline:after': {
            left: theme.spacing(4),
        },
    },
}));

/**
 * Prepare a copy of a test type for the "Save As" / copy flow.
 * No-op when not in copy mode. Returns a deep-cloned object so the caller is free
 * to keep mutating it downstream without affecting the API response or any cache.
 */
function prepareCopiedTestType(source, isCopyMode) {
    if (!isCopyMode) {
        return source;
    }

    const copied = _.cloneDeep(source || {});
    delete copied._id;
    _.unset(copied, 'details.icon');

    const copySuffix = `(${t`Copy`})`;
    const baseName = _.get(copied, 'name', '');
    const baseTitle = _.get(copied, 'details.title', '');
    copied.name = `${baseName} ${copySuffix}`;
    copied.group = copied.name;
    _.set(copied, 'details.title', `${baseTitle} ${copySuffix}`);

    delete copied.createdAt;
    delete copied.updatedAt;

    return copied;
}

function TypeDetailPage() {
    const classes = useStyles();
    const { search } = useLocation();
    const [showExample, setShowExample] = useState(false);
    const [showSource, setShowSource] = useState(false);
    const [configIsDirty, setConfigIsDirty] = useState(false);
    const [chart2axis, setChart2axis] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [uploadingScript, setUploadingScript] = useState(false);
    const [pendingUploadedScript, setPendingUploadedScript] = useState(null);
    const exampleScript = useMemo(() => buildExampleScript('example'), []);
    const hasTestInstances = useRef(false);
    const userOkedBreakingTests = useRef(false);
    const pendingSaveRef = useRef(null);
    const isCopyMode = useMemo(() => {
        const params = new URLSearchParams(search || '');
        return params.get('copy') === 'copy';
    }, [search]);

    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        mode: 'onChange',
        defaultValues,
    });
    const { handleSubmit, setError, clearErrors, reset, setValue, getValues, register } = methods;
    const watchedMetrics = methods.watch(TEST_TYPE_METRICS_FIELD);
    const watchedThresholds = methods.watch(TEST_TYPE_THRESHOLDS_FIELD);
    const watchedIconName = methods.watch(TEST_TYPE_ICON_FIELD);
    const watchedBttTest = !!methods.watch(TEST_TYPE_BTT_TEST_FIELD);
    const watchedScript = methods.watch(TEST_TYPE_SCRIPT_FIELD);

    const testTypeName = useEntityIdParam();
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();
    const formSaveService = useFormSaveService();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const { errorWrapper } = useErrorWrapper();
    const isNew = !testTypeName;
    const queryTestTypeFunction = async () => {
        if (isNew) {
            return defaultValues;
        }
        const response = await dataService.get(ADMIN_TEST_TYPES_URL, {
            query: JSON.stringify({ name: testTypeName }),
        });

        if (_.isArray(response)) {
            return _.first(response);
        }

        return response;
    };

    const postFindHandler = (response) => {
        const preparedResponse = prepareCopiedTestType(response, isCopyMode);
        const yAxis = _.get(preparedResponse, TEST_TYPE_CHART_Y_AXIS_FIELD);
        const hasSecondAxis = _.isArray(yAxis) ? yAxis.length > 1 : !_.isNil(_.get(yAxis, '1'));
        setChart2axis(hasSecondAxis);

        const stacking = _.get(preparedResponse, TEST_TYPE_CHART_STACKING_FIELD);
        if (_.isNil(stacking) || stacking === '') {
            _.set(preparedResponse, TEST_TYPE_CHART_STACKING_FIELD, CHART_STACKING_DEFAULT_KEY);
        }

        if (!_.isArray(_.get(preparedResponse, TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD))) {
            _.set(preparedResponse, TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD, []);
        }

        _.defaults(preparedResponse, {
            thresholds: [],
            converted: false,
            originalScript: '',
            scriptConvertErr: '',
            metricsFromSteps: [],
        });

        if (!preparedResponse.originalScript && !preparedResponse.converted) {
            preparedResponse.originalScript = preparedResponse.script || '';
        }

        return preparedResponse;
    };

    const { initialized, finding, formData } = useFind({
        queryFunc: queryTestTypeFunction,
        postFindHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}),
    });

    const queueUploadedScript = useCallback((scriptText) => {
        setPendingUploadedScript({ id: Date.now(), scriptText });
        return scriptText;
    }, []);

    useEffect(() => {
        // These values are written programmatically during script conversion and save,
        // so they must stay registered even though they do not have always-mounted inputs.
        register('name');
        register('group');
        register('originalScript');
        register('converted');
        register('scriptConvertErr');
        register('metricsFromSteps');
    }, [register]);

    const handleTitleChange = useCallback(
        (e) => {
            const normalizedName = toLowerCamelCase(e?.target?.value || '');

            setValue('name', normalizedName, { shouldDirty: true });
            setValue('group', normalizedName, { shouldDirty: true });
        },
        [setValue]
    );

    const showScriptConversionAlert = useCallback(
        (message) => {
            alertsActions.createErrorAlert(`Error converting script to Python 3: ${message || 'Unknown error'}`);
        },
        [alertsActions]
    );

    useEffect(() => {
        if (!pendingUploadedScript?.scriptText) {
            return undefined;
        }

        let active = true;

        const processUploadedScript = async () => {
            setUploadingScript(true);
            setValue('originalScript', pendingUploadedScript.scriptText, { shouldDirty: true });
            setValue('converted', false, { shouldDirty: true });
            setValue('scriptConvertErr', '', { shouldDirty: true });
            setValue('metricsFromSteps', [], { shouldDirty: true });

            try {
                const response = await convertScript(dataService, pendingUploadedScript.scriptText);
                if (!active) {
                    return;
                }

                if (response?.code !== 0) {
                    const errorMessage = response?.message || 'Unknown error';

                    clearErrors(TEST_TYPE_SCRIPT_FIELD);
                    setValue('scriptConvertErr', errorMessage, { shouldDirty: true });
                    showScriptConversionAlert(errorMessage);
                    return;
                }

                const convertedScript = response?.script || pendingUploadedScript.scriptText;
                const nextMetrics = mergeMetricsFromScript(
                    getValues(TEST_TYPE_METRICS_FIELD) || [],
                    pendingUploadedScript.scriptText,
                    response?.stepMetrics || []
                );

                clearErrors(TEST_TYPE_SCRIPT_FIELD);
                setValue(TEST_TYPE_SCRIPT_FIELD, convertedScript, { shouldDirty: true, shouldValidate: true });
                setValue('converted', true, { shouldDirty: true });
                setValue('scriptConvertErr', '', { shouldDirty: true });
                setValue('metricsFromSteps', response?.stepMetrics || [], { shouldDirty: true });
                setValue(TEST_TYPE_SLICE_PACKETS_FIELD, !isBttScript(convertedScript), {
                    shouldDirty: true,
                    shouldValidate: true,
                });
                setValue(TEST_TYPE_METRICS_FIELD, nextMetrics, { shouldDirty: true, shouldValidate: true });
            } catch (error) {
                if (!active) {
                    return;
                }

                setValue(
                    'scriptConvertErr',
                    error?.responseBody?.message || error?.message || 'Script conversion failed',
                    {
                        shouldDirty: true,
                    }
                );
                clearErrors(TEST_TYPE_SCRIPT_FIELD);
                showScriptConversionAlert(error?.responseBody?.message || error?.message || 'Script conversion failed');
            } finally {
                if (active) {
                    setUploadingScript(false);
                    setPendingUploadedScript(null);
                }
            }
        };

        processUploadedScript();

        return () => {
            active = false;
        };
    }, [pendingUploadedScript, dataService, setError, clearErrors, setValue, getValues, showScriptConversionAlert]);

    // Look up whether this test type has existing test instances (mirrors portal/ui lookForTestInstances)
    useEffect(() => {
        if (!testTypeName) {
            hasTestInstances.current = false;
            return;
        }
        hasTestInstancesForType(dataService, testTypeName)
            .then((result) => {
                hasTestInstances.current = result;
            })
            .catch(() => {
                hasTestInstances.current = false;
            });
    }, [dataService, testTypeName]);

    // Mirrors portal/ui buildSpecificTypeAttributes: add wi-fi input types for wifiConnectTest
    const inputTypes = useMemo(
        () => (formData?.wifiConnectTest ? [...BASE_INPUT_TYPES, ...WIFI_INPUT_TYPES] : BASE_INPUT_TYPES),
        [formData?.wifiConnectTest]
    );

    const breadcrumbs = useBreadcrumb(
        [{ link: goToUrl, text: t`Service Tests` }],
        testTypeName,
        formData,
        TEST_TYPE_TITLE_FIELD,
        t`New`
    );

    /**
     * Determines whether save‐time changes may break existing tests.
     * Mirrors portal/ui warnAboutChanges → areFieldsDirty(true).
     */
    const shouldWarnAboutChanges = useCallback(
        (submitData) => {
            if (isNew || !hasTestInstances.current || userOkedBreakingTests.current) {
                return false;
            }

            const origMetrics = JSON.stringify(formData?.[TEST_TYPE_METRICS_FIELD] || []);
            const currMetrics = JSON.stringify(submitData?.[TEST_TYPE_METRICS_FIELD] || []);
            if (origMetrics !== currMetrics) return true;

            const origSections = JSON.stringify(_.get(formData, TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD, []));
            const currSections = JSON.stringify(_.get(submitData, TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD, []));
            if (origSections !== currSections) return true;

            const origIsBtt = isBttScript(formData?.script);
            const currIsBtt = isBttScript(submitData?.script);
            if (origIsBtt !== currIsBtt) return true;

            return false;
        },
        [isNew, formData]
    );

    /** Persist the payload via formSaveService. */
    const persistSave = useCallback(
        (payload) => {
            return formSaveService
                .save({
                    url: ADMIN_TEST_TYPES_URL,
                    recordId: formData?._id,
                    dataToSave: payload,
                    setError,
                })
                .then(async (response) => {
                    if (!payload?.scriptConvertErr) {
                        return response;
                    }

                    const savedTestTypeId = response?._id || payload?._id;
                    if (savedTestTypeId) {
                        await disableTestsForType(dataService, savedTestTypeId);
                    }

                    return response;
                });
        },
        [formSaveService, formData, setError, dataService]
    );

    const handleFormSubmit = (submitData) => {
        // Reset so that a subsequent submit (e.g. after a failed save) will re-evaluate the warning.
        userOkedBreakingTests.current = false;

        const normalizedName = toLowerCamelCase(_.get(submitData, TEST_TYPE_TITLE_FIELD));
        if (!normalizedName) {
            setError(TEST_TYPE_TITLE_FIELD, {
                type: 'validate',
                message: t`Name must include at least one letter or number.`,
            });
            return throwValidationError();
        }

        const metrics = submitData?.[TEST_TYPE_METRICS_FIELD] || [];
        if (!metrics.some((metric) => metric.isMain)) {
            setError(TEST_TYPE_METRICS_FIELD, { type: 'validate', message: t`A Primary Metric is required.` });
            return throwValidationError();
        }

        // Enrich thresholds (mirrors portal/ui save) — avoid mutating RHF's submitData directly.
        const thresholds = _.get(submitData, TEST_TYPE_THRESHOLDS_FIELD, []);
        const enrichedData = { ...submitData };
        if (thresholds.length > 0) {
            enrichedData.allowAutomaticThresholds = true;
            enrichedData[TEST_TYPE_THRESHOLDS_FIELD] = thresholds.map((threshold) => ({
                notificationDelay: 300,
                notificationEnabled: true,
                ...threshold,
            }));
        }

        // Build payload (normalizes axes, name, group)
        const payload = buildTestTypePayload({ ...formData, ...enrichedData }, chart2axis);

        // Clean up axes and metrics (mirrors portal/ui cleanUpAxesAndMetrics)
        cleanUpAxesAndMetrics(payload);

        // If changes may break existing tests, show warning dialog
        if (shouldWarnAboutChanges(submitData)) {
            pendingSaveRef.current = payload;
            setShowWarningDialog(true);
            return;
        }

        // Persist
        return persistSave(payload);
    };

    /** Called when user confirms the warning dialog. */
    const handleWarningContinue = useCallback(() => {
        userOkedBreakingTests.current = true;
        setShowWarningDialog(false);
        const payload = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (payload) {
            return persistSave(payload);
        }
    }, [persistSave]);

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    // Mirrors TestTypesListPanel delete behavior: soft-delete via PUT { status: 'Deleted' },
    // then navigate back to the list page. Reuses deleteWrapper for loading/nav/error handling.
    const handleConfirmedDelete = useCallback(() => {
        if (!formData?._id) {
            return undefined;
        }
        return deleteWrapper({
            deleteFunction: () => dataService.put(`${ADMIN_TEST_TYPES_URL}/${formData._id}`, { status: 'Deleted' }),
            onDeleteGoTo: goToUrl,
            errorHandler: errorWrapper({}),
        })();
    }, [deleteWrapper, dataService, formData, errorWrapper]);

    const dirtyCheck = (isDirty) => isDirty || configIsDirty;

    const handleMetricDelete = async (remainingMetrics = [], deletedMetric = {}) => {
        const deletedMetricId = _.toNumber(deletedMetric?.id);
        if (deletedMetricId <= 0) {
            return remainingMetrics;
        }

        // Match portal/ui removeMetric: remove thresholds referencing the deleted metric id.
        const nextThresholds = _.filter(watchedThresholds || [], (threshold) => {
            return _.toNumber(_.get(threshold, 'metricIds[0]')) !== deletedMetricId;
        });

        if (nextThresholds.length !== (watchedThresholds || []).length) {
            setValue(TEST_TYPE_THRESHOLDS_FIELD, nextThresholds, { shouldDirty: true });
            setConfigIsDirty(true);
        }

        return remainingMetrics;
    };
    const refreshMetrics = (metrics) => {
        if (metrics.some((metric) => metric.isMain)) {
            clearErrors(TEST_TYPE_METRICS_FIELD);
        }
        return metrics;
    };

    const availableThresholdMetrics = useMemo(() => {
        return _.filter(watchedMetrics, (metric) => {
            return !RESERVED_METRICS.includes(_.toLower(metric?.name || '')) && !metric?.isString;
        });
    }, [watchedMetrics]);

    const activeMetricColumns = useMemo(() => {
        if (!watchedBttTest) {
            return metricColumns;
        }
        return [
            ...metricColumns,
            {
                // `beaconStep` is a test-type default for BTT step metrics. Test instances later
                // turn these defaults into per-test `beaconSteps[*].enabled` selections.
                type: COLUMN_TYPES.TEXT,
                mapping: 'beaconStep',
                title: t`SEM Metric Enabled`,
                width: 140,
                formatter: (value) => _.capitalize(value),
            },
        ];
    }, [watchedBttTest]);

    const exportScriptPackage = useCallback(
        async (name, script) => {
            const queryParams = {
                t: Date.now(),
                query: JSON.stringify({ name }),
            };

            try {
                const response = await dataService.post(
                    '/ipm/v1c/testTypesScriptExport/exportScript',
                    { script },
                    queryParams
                );
                const blob = await normalizeDownloadBlob(response);
                saveAs(blob, `${name}.zip`);
            } catch (error) {
                errorWrapper()(error);
            }
        },
        [dataService, errorWrapper]
    );

    const handleDownloadExample = useCallback(() => {
        return exportScriptPackage(EXAMPLE_SCRIPT_TITLE, exampleScript);
    }, [exampleScript, exportScriptPackage]);

    const handleDownloadPackage = async () => {
        const name = toLowerCamelCase(_.get(getValues(), TEST_TYPE_TITLE_FIELD)) || 'downloadPackage';
        return exportScriptPackage(name, watchedScript);
    };

    const handleToggleShowSource = () => {
        setShowSource((current) => {
            const next = !current;
            if (next) {
                setShowExample(false);
            }
            return next;
        });
    };

    const handleToggleShowExample = () => {
        setShowExample((current) => {
            const next = !current;
            if (next) {
                setShowSource(false);
            }
            return next;
        });
    };

    const handleToggleChart2Axis = (_e, checked) => {
        setChart2axis(checked);
        setConfigIsDirty(true);
    };

    const thresholdColumns = useMemo(() => getThresholdColumns(watchedMetrics), [watchedMetrics]);

    return (
        <AdminPage
            pageId={buildPageId(functionName)}
            breadcrumbs={breadcrumbs}
            actions={
                <Actions>
                    {!isNew && (
                        <Button
                            onClick={() => setShowDeleteDialog(true)}
                            disabled={saving || deleting || uploadingScript}
                        >
                            <Trans>Delete</Trans>
                        </Button>
                    )}
                    <Button
                        onClick={handleSubmit(handleSubmitFunction)}
                        variant="contained"
                        color="primary"
                        disabled={saving || deleting || uploadingScript}
                    >
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <>
                <FormProvider {...methods}>
                    <NgpForm
                        submitFunction={handleSubmitFunction}
                        initialized={initialized}
                        loading={finding || saving || deleting}
                        isDirtyFunc={dirtyCheck}
                    >
                        <FormSection title={<Trans>Test Type Info</Trans>}>
                            <NgpTextField
                                fieldName={TEST_TYPE_TITLE_FIELD}
                                label={<Trans>Name</Trans>}
                                required
                                onChangeFunc={handleTitleChange}
                                inputProps={{ size: 50, maxLength: 50 }}
                            />
                            <NgpTextField
                                fieldName={TEST_TYPE_ICON_FIELD}
                                label={<Trans>Icon</Trans>}
                                required
                                className={classes.iconField}
                                onChangeFunc={(e) => {
                                    const rawValue = `${e?.target?.value || ''}`;
                                    const normalizedValue = rawValue.replace(/ /g, '_');

                                    if (normalizedValue !== rawValue) {
                                        setValue(TEST_TYPE_ICON_FIELD, normalizedValue, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icon>{`${watchedIconName || ''}`.trim()}</Icon>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormHelperText>
                                <Trans>Enter the name of an icon found on the</Trans>{' '}
                                <a href="https://fonts.google.com/icons" target="_blank" rel="noopener noreferrer">
                                    Material Icons Page
                                    <OpenInNew
                                        style={{
                                            fontSize: 18,
                                            marginLeft: 4,
                                            verticalAlign: 'text-bottom',
                                        }}
                                    />
                                </a>
                            </FormHelperText>
                            <NgpTextarea
                                fieldName={TEST_TYPE_DESCRIPTION_FIELD}
                                label={<Trans>Description</Trans>}
                                cols={60}
                            />
                            <NgpNativeNumberField
                                fieldName={TEST_TYPE_TIMEOUT_FIELD}
                                label={<Trans>Service Test Timeout(from 45 to 900 seconds)</Trans>}
                                required
                                min={45}
                                max={900}
                                inputWidth={'40ch'}
                                helpTipText={t`The time the nPoint waits for a Service Test to complete.  The default is 45 seconds, but the amount of time may be increased if the Service Test needs more time to complete.`}
                            />
                        </FormSection>

                        <FormSection title={<Trans>Smart Edge Monitoring</Trans>}>
                            <NgpToggle
                                fieldName={TEST_TYPE_SLICE_PACKETS_FIELD}
                                label={<Trans>Slice captured packets to reduce network utilization</Trans>}
                            />
                        </FormSection>

                        <FormSection title={<Trans>Script</Trans>}>
                            <Typography variant="body2" style={{ margin: '16px 0' }}>
                                Please provide a Python script which will be run to collect custom metrics from your
                                nPoints.
                            </Typography>

                            <RowFlexLayout vTop>
                                <NgpFileUpload
                                    fieldName={TEST_TYPE_SCRIPT_FIELD}
                                    label={
                                        <div title={t`Python Recommended`}>
                                            <Trans>Upload Script</Trans>
                                        </div>
                                    }
                                    showFileName={false}
                                    accept=".py"
                                    required
                                    maxFileSize={MAX_SCRIPT_FILE_SIZE_ALLOWED}
                                    convertValue={queueUploadedScript}
                                    buttonProps={{
                                        color: 'primary',
                                        variant: 'text',
                                        size: 'medium',
                                        disabled: uploadingScript,
                                    }}
                                />

                                {!!watchedScript && (
                                    <Button
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        onClick={handleDownloadPackage}
                                        startIcon={<CloudDownload />}
                                    >
                                        <Trans>Download Package</Trans>
                                    </Button>
                                )}

                                {!!watchedScript && (
                                    <Button color="primary" onClick={handleToggleShowSource} style={{ marginRight: 8 }}>
                                        {showSource ? <Trans>Hide Script</Trans> : <Trans>View Script</Trans>}
                                    </Button>
                                )}

                                <div style={{ flex: 1 }} />

                                <Button color="primary" onClick={handleToggleShowExample} style={{ marginRight: 8 }}>
                                    {showExample ? <Trans>Hide Example</Trans> : <Trans>View Example</Trans>}
                                </Button>

                                <Button
                                    style={{ marginRight: 8 }}
                                    color="primary"
                                    onClick={handleDownloadExample}
                                    startIcon={<CloudDownload />}
                                >
                                    <Trans>Download Example</Trans>
                                </Button>
                            </RowFlexLayout>

                            {uploadingScript && <LinearProgress style={{ marginTop: 8 }} />}

                            {(showExample || (showSource && !!watchedScript)) && (
                                <pre
                                    style={{
                                        margin: '5px',
                                        whiteSpace: 'pre-wrap',
                                        backgroundColor: 'rgba(0, 0, 0, .05)',
                                        maxHeight: 600,
                                        overflow: 'auto',
                                        padding: 16,
                                    }}
                                >
                                    {showExample ? exampleScript : watchedScript}
                                </pre>
                            )}
                        </FormSection>

                        <FormSection title={<Trans>Metrics</Trans>}>
                            <Typography variant="body2" style={{ margin: '8px 0 12px 0' }}>
                                <Trans>
                                    Metrics you expect the script to record. Metric names can only be letters, numbers
                                    and underscores. An example metric name would be {'"test_output_metric"'}.
                                </Trans>
                            </Typography>
                            <ListFormField
                                fieldName={TEST_TYPE_METRICS_FIELD}
                                // Use the watched form value so convertScript-driven setValue(...) updates
                                // can refresh the list immediately without waiting for formData to reload.
                                dbSelected={watchedMetrics}
                                addButtonText={t`Add Metric`}
                                gridColumns={activeMetricColumns}
                                setSelectionIsDirty={setConfigIsDirty}
                                formComponent={<MetricsDialogForm chart2axis={chart2axis} />}
                                postConfirmHandler={refreshMetrics}
                                postDeleteHandler={handleMetricDelete}
                                emptyGridText={t`Click the Add Metric button above to add metrics`}
                                visibleRows={20}
                            />
                        </FormSection>

                        {(watchedMetrics?.length || 0) > 0 && (
                            <FormSection title={<Trans>Metric Thresholds</Trans>} addSpacer>
                                <ListFormField
                                    fieldName={TEST_TYPE_THRESHOLDS_FIELD}
                                    // Thresholds follow the current RHF value for the same reason as metrics.
                                    dbSelected={watchedThresholds}
                                    addButtonText={t`Add Threshold`}
                                    gridColumns={thresholdColumns}
                                    setSelectionIsDirty={setConfigIsDirty}
                                    formComponent={
                                        <ThresholdsDialogForm availableMetrics={availableThresholdMetrics} />
                                    }
                                    emptyGridText={t`Click the Add Threshold button above to add thresholds`}
                                    visibleRows={20}
                                />
                            </FormSection>
                        )}

                        {!watchedBttTest && (
                            <FormSection title={<Trans>Authentication</Trans>}>
                                {/*
                                 BTT test types always show the authentication section on test instances.
                                 This toggle exists only for non-BTT types that still need to opt into that UI.
                                */}
                                <NgpToggle
                                    fieldName={TEST_TYPE_SHOW_BTT_AUTH_FIELD}
                                    label={<Trans>Show Authentication section in test instances</Trans>}
                                />
                            </FormSection>
                        )}

                        <FormSection title={<Trans>Chart Settings</Trans>}>
                            <FormControlLabel
                                control={<Switch checked={chart2axis} onChange={handleToggleChart2Axis} />}
                                label={<Trans>Use 2-axis chart</Trans>}
                            />

                            <NgpTextField
                                fieldName={TEST_TYPE_CHART_Y_AXIS_TITLE_LEFT_FIELD}
                                label={<Trans>Y-Axis Title</Trans>}
                                required
                                inputProps={{ maxLength: 35, size: 35 }}
                            />

                            {chart2axis && (
                                <NgpTextField
                                    fieldName={TEST_TYPE_CHART_Y_AXIS_TITLE_RIGHT_FIELD}
                                    label={<Trans>Y-Axis Title (Right)</Trans>}
                                    required
                                    inputProps={{ maxLength: 35, size: 35 }}
                                />
                            )}

                            <NgpTextField
                                fieldName={TEST_TYPE_CHART_TYPE_FIELD}
                                label={<Trans>Chart Type</Trans>}
                                select
                            >
                                {CHART_TYPES.map((chartType) => (
                                    <MenuItem key={chartType} value={chartType}>
                                        {_.startCase(chartType)}
                                    </MenuItem>
                                ))}
                            </NgpTextField>

                            <NgpSelect fieldName={TEST_TYPE_CHART_STACKING_FIELD} label={<Trans>Chart Stacking</Trans>}>
                                {CHART_STACKINGS.map((stackingOption) => (
                                    <MenuItem key={stackingOption.key} value={stackingOption.key}>
                                        {stackingOption.label}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                        </FormSection>

                        <TestInstanceTemplateSection
                            sectionsFieldName={TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD}
                            inputTypes={inputTypes}
                            setConfigIsDirty={setConfigIsDirty}
                        />
                    </NgpForm>
                </FormProvider>

                <NgpTwoButtonDialog
                    title={<Trans>Are you sure?</Trans>}
                    confirmButtonText={<Trans>Continue</Trans>}
                    cancelButtonText={<Trans>Cancel</Trans>}
                    open={showWarningDialog}
                    setOpen={setShowWarningDialog}
                    onConfirm={handleWarningContinue}
                    isWarning
                    closeInTitleBar={false}
                >
                    <Typography>
                        <Trans>
                            WARNING: The changes you have made to this Test Type MAY break existing tests. You may need
                            to update existing tests to take in these changes.
                        </Trans>
                    </Typography>
                </NgpTwoButtonDialog>

                <TestTypeDeleteDialog
                    open={showDeleteDialog}
                    setOpen={setShowDeleteDialog}
                    onConfirm={handleConfirmedDelete}
                />
            </>
        </AdminPage>
    );
}

/**
 * BTW: For Type List, would use /plm/{plmId}/service-tests/wcDetails/test/
 *
 * Routes:
 * - Type Create: /plm/{plmId}/service-tests/new
 * - Type Edit:   /plm/{plmId}/service-tests/wcDetails
 * - Test Edit:   /plm/{plmId}/service-tests/_id
 */
export default function TestTypesDetailPage() {
    const recordId = useEntityIdParam();

    if (isUUID(recordId)) {
        // Support /plm/{plmId}/service-tests/_id
        return <TestIdPage />;
    }

    return <TypeDetailPage />;
}
